<template>
  <!-- State: No Files | No Results | Error -->
  <div class="h-full flex justify-center" v-show="state">
    <slot name="state" />
  </div>

  <!-- Data: List -->
  <div class="flex flex-col">
    <File
      v-for="item in files"
      :key="item.id"
      :item="item"
      @click="handleClickFile(item)"
    />
    <div v-if="isLoading">
      <Skeleton v-for="i of [...Array(10).keys()]" :key="i" />
    </div>
    <span
      ref="loadMoreTrigger"
      v-show="isLoading || files.length"
      class="w-full h-px block"
    />
  </div>
</template>

<script setup>
// Vue utilities
import { computed, onMounted, onUnmounted, ref, watch, inject } from "vue";
import { storeToRefs } from "pinia";
import { useIntersectionObserver } from "@vueuse/core";

// Importing utility libraries
import axios from "axios";
import route from "ziggy-js";

// Import Components
import { File, Skeleton } from "@/SharedComponents/Entities/File";

// Importing composables
import { useFilters } from "@/Composables/useFilters";
import { useSearch } from "@/Composables/useSearch";
import { useSorting } from "@/Composables/useSorting";

// Emits
const emit = defineEmits([
  "show:detail",
  "update:count",
  "update:fetchingStatus",
]);

// Emitter
const emitter = inject("emitter");

// Sorting
const sortingStore = useSorting("files");
const { sortingValues } = storeToRefs(sortingStore);

// Filters
const globalFilterStore = useFilters("global");
const { filterValues: globalFilterValues } = storeToRefs(globalFilterStore);
const filterStore = useFilters("files");
const { filterValues } = storeToRefs(filterStore);

// Search
const searchStore = useSearch("files");
const { searchValues } = storeToRefs(searchStore);

// Define Props
const props = defineProps({
  count: { type: Number, default: 0 },
  fetchingStatus: { type: String, required: true },
});

// Refs
const page = ref(null);
const perPage = ref(25);
const files = ref([]);
const totalFiles = ref(0);
const isLoading = ref(false);
const loadMoreTrigger = ref(null);
const isIntersectingLoadMoreTrigger = ref(false);
const controller = ref(null);

// Get Data
async function getFiles() {
  if (fullyLoaded.value) {
    return;
  }

  // Cancel any running requests before making a new one
  if (isLoading.value && controller.value) {
    console.log("Aborting previous request...");
    controller.value.abort();
  }

  // Assign new controller to send new requests with different signal
  controller.value = new AbortController();

  isLoading.value = true;

  emit("update:fetchingStatus", "loading");

  const parameters = {
    page: page.value?.meta?.current_page + 1 || 1,
    ...sortingValues.value,
    ...filters.value,
    per_page: perPage.value,
  };

  const url = route("api.documents.search");
  await axios
    .post(
      url,
      {
        ...parameters,
      },
      {
        signal: controller.value.signal,
      }
    )
    .then((response) => {
      page.value = response.data;
      files.value = [...files.value, ...response.data.data];
      totalFiles.value = response.data.meta.total;

      emit("update:count", response.data.meta.total);
      emit("update:fetchingStatus", "loaded");

      // Give everything time to re-render before checking again
      setTimeout(() => {
        if (isIntersectingLoadMoreTrigger.value) {
          getFiles();
        }
      }, 100);
    })
    .catch((err) => {
      console.log("error occurred", err);
      emit("update:count", 0);
      emit("update:fetchingStatus", "error");
    })
    .finally(() => {
      isLoading.value = false;
    });
}

// Lifecycle hooks
onMounted(() => {
  getFiles();
});

onUnmounted(() => {
  stop();
});

// Computed Props
const filters = computed(() => ({
  ...sortingValues.value,
  ...searchValues.value,
  ...filterValues.value,
  tenant: globalFilterValues.value.tenant,
  tenant_only: filterValues.value.tenant?.length > 0,
}));

const state = computed(() => {
  if (props.fetchingStatus === "loading") {
    return false;
  }

  return props.count === 0 || props.fetchingStatus === "error";
});

const fullyLoaded = computed(() => {
  return !!(
    page.value?.meta &&
    page.value?.meta.last_page === page.value?.meta.current_page
  );
});

// Watchers
watch(
  filters,
  (newVal, oldVal) => {
    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      page.value = null;
      files.value = [];
      getFiles();
    }
  },
  { deep: true }
);

// Functions
const { stop } = useIntersectionObserver(
  loadMoreTrigger,
  ([{ isIntersecting }]) => {
    isIntersectingLoadMoreTrigger.value = isIntersecting;
    // Don't trigger multiple times while running
    if (isIntersecting && !isLoading.value) {
      getFiles();
    }
  }
);

const handleClickFile = (file) => {
  emit("show:detail", file);
};

// Emitters
emitter.on("update-files-data", (e) => {
  files.value = files.value.map((file) => {
    const updatedFile = e.data.find((f) => f.id === file.id);
    return updatedFile ? updatedFile : file;
  });
});

emitter.on("remove-files-data", (e) => {
  files.value = files.value.filter((file) => !e.data.includes(file.id));
  totalFiles.value -= e.data.length;
});
</script>
